import { Monster } from "../api";
import { MonsterInfoButton, MonsterInspectionEnabled } from './MonsterInspector';
import StatBreakdown from "./StatBreakdown";
import { useMonsterTokenStyle } from "../utils/clientSettings";
import { getMonsterEffectiveMovementType, MonsterTokenBack, MonsterTokenFrontSvg } from "./MonsterTokenSvg";

import './Monsters.css';
import { DunwichHorrorToken } from './DunwichHorrorToken';

const getMonsterImagePaths = (monsterName: string) => {
  const normalizedName = monsterName.replace(/\s+/g, '');
  return {
    front: `/Monsters/${normalizedName}.png`,
    back: `/Monsters/${normalizedName}Back.png`,
  };
};

interface MonsterDetailCardProps {
  monster: Monster;
  index?: number;
  cls?: string;
  tooltipPosition?: string;
  stopPropagation?: boolean;
}

export const MonsterDetailCard: React.FC<MonsterDetailCardProps> = ({ monster, index = 0, cls = '', tooltipPosition, stopPropagation }) => {
  const monsterImages = getMonsterImagePaths(monster.name);
  const monsterTokenStyle = useMonsterTokenStyle();
  const isDigitalToken = monsterTokenStyle === 'Digital';
  const breakdowns = [
    { label: 'Toughness', data: monster.toughnessBreakdown },
    { label: 'Endless', data: monster.endlessBreakdown },
    { label: 'Combat Rating', data: monster.combatRatingBreakdown },
    { label: 'Combat Damage', data: monster.combatDamageBreakdown },
    { label: 'Horror Rating', data: monster.horrorRatingBreakdown },
    { label: 'Horror Damage', data: monster.horrorDamageBreakdown },
    { label: 'Awareness', data: monster.awarenessBreakdown },
  ].filter(entry => (entry.data?.modifiers?.length || 0) > 0);
  const margin = tooltipPosition === 'left' ? 35 : 120; 

  return (
    <div
      className={`monster-detail-card ${cls}`}
      style={{
        marginLeft: `${index * margin * 0.2}px`
      }}
    >
      <div className="monster-detail-images">
        {isDigitalToken && monster.name === 'The Dunwich Horror' ? <DunwichHorrorToken monster={monster} stopPropagation={stopPropagation} /> : isDigitalToken ? (
          <>
            <MonsterTokenFrontSvg monster={monster} stopPropagation={stopPropagation} />
            <MonsterTokenBack monster={monster} stopPropagation={stopPropagation} />
          </>
        ) : (
          <>
            <div className="monster-info-front"><img loading="lazy" src={monsterImages.front} alt={`${monster.name} front`} /><MonsterInfoButton monster={monster} /></div>
            <img loading="lazy" src={monsterImages.back} alt={`${monster.name} back`} />
          </>
        )}
      </div>
      {breakdowns.length > 0 && !isDigitalToken && (
        <div className="monster-breakdown-list">
          {breakdowns.map((entry) => (
            <div key={entry.label} className="monster-breakdown-entry">
              <div className="monster-breakdown-label">{entry.label}</div>
              <StatBreakdown breakdown={entry.data} emptyLabel="No modifiers" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface MonsterMarkerProps {
  monster: Monster;
  index?: number;
  monsterSize?: number;
  monsterStyle?: React.CSSProperties;
  showTooltip?: boolean;
  showInfo?: boolean;
  stopPropagation?: boolean;
  tooltipPosition?: string;
}

// export const MonsterBothSides: React.FC<MonsterMarkerProps> = ({
//   monster,
//   index = 0,
//   monsterSize = 60,
//   monsterStyle = {},
//   showTooltip = true,
//   stopPropagation = false,
//   tooltipPosition = 'right',
// }) => {
  
// }

export const MonsterMarker: React.FC<MonsterMarkerProps> = ({
  monster,
  index = 0,
  monsterSize = 60,
  monsterStyle = {},
  showTooltip = true,
  stopPropagation = false,
  tooltipPosition = 'right',
  showInfo = true,
}) => {
  const movementTypeClass = `monster-movement-${getMonsterEffectiveMovementType(monster).toLowerCase()}`;
  const monsterImages = getMonsterImagePaths(monster.name);
  const monsterTokenStyle = useMonsterTokenStyle();
  const isDigitalToken = monsterTokenStyle === 'Digital';

  return (
    <div
      className={`monster-marker ${movementTypeClass}${isDigitalToken ? ' monster-marker-digital' : ''}`}
      style={{
        width: `${monsterSize}px`,
        height: `${monsterSize}px`,
        ...monsterStyle,
      }}
      onClick={(e) => {
        if (stopPropagation) {
          e.stopPropagation();
        }
      }}
    >
      {isDigitalToken ? (
        <MonsterTokenFrontSvg monster={monster} showInfo={showInfo} />
      ) : (
        <div className="monster-info-front"><img loading="lazy" src={monsterImages.front} alt={`${monster.name} token`} className="monster-icon" />{showInfo && <MonsterInfoButton monster={monster} />}</div>
      )}
      {showTooltip && (
        <div className={`monster-tooltip-${tooltipPosition}`} >
          <MonsterInspectionEnabled.Provider value={false}><MonsterDetailCard monster={monster} index={index} tooltipPosition={tooltipPosition} /></MonsterInspectionEnabled.Provider>
        </div>
      )}
    </div>
  );
};

export const MonstersDetails = ({ monsters, stopPropagation = false }: { monsters: Monster[], stopPropagation?: boolean}) => {
  return (
    <div className="combat-monsters-wrapper">
      {monsters.map((m) => (
        <MonsterDetailCard monster={m} cls="monster-action" stopPropagation={stopPropagation} />
      ))}
    </div>
  )
}

export const Monsters = ({ monsters, monsterSize = 60, wrapperClass = '', wrapperStyle, monsterStyle = {}, tooltipPosition, showInfo = true }:
  { wrapperClass?: any; wrapperStyle?: any; monsters: Monster[], monsterSize?: any, monsterStyle?: any, tooltipPosition?: string, showInfo?: boolean }) => {

    if (monsters.length === 0) {
      return null;
    }
    return (
      <div className={`monsters ${wrapperClass}`} style={wrapperStyle}>
        {monsters.map((monster, index) => {
          return (
            <div key={`${monster.name}-${index}`} className="monster" style={{
              width: `${monsterSize / 3}px`
            }}>
              <MonsterMarker
                key={`${monster.name}-${index}`}
                monster={monster}
                index={index}
                monsterSize={monsterSize}
                monsterStyle={monsterStyle}
                tooltipPosition={tooltipPosition}
                showInfo={showInfo}
                stopPropagation={true}
              />
            </div>
          );
        })}
        <div style={{
          width: `${monsterSize * 2 / 3}px`
        }} />
      </div>
    );
}
